import { useCallback, useState } from 'react';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { idempotencyStore } from './idempotency-store';

export interface IdempotentMutationContext {
  idempotencyKey: string;
}

export type IdempotentMutationFn<TVars, TData> = (
  vars: TVars,
  ctx: IdempotentMutationContext,
) => Promise<TData>;

export interface UseIdempotentActionOptions<TVars, TData, TError>
  extends Omit<UseMutationOptions<TData, TError, TVars>, 'mutationFn' | 'retry' | 'scope'> {
  scope: string;
  scopeId?: string;
  resetOnSuccess?: boolean;
}

export interface UseIdempotentActionResult<TVars, TData, TError> {
  execute: (vars: TVars) => void;
  executeAsync: (vars: TVars) => Promise<TData>;
  reset: () => void;
  idempotencyKey: string;
  status: 'idle' | 'pending' | 'success' | 'error';
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  data: TData | undefined;
  error: TError | null;
}

/**
 * Idempotent action hook with automatic retry and deduplication
 *
 * @taskId HOUSEKEEPING
 * @see Code quality - ESLint fix (derived state)
 */
export function useIdempotentAction<TVars, TData, TError = unknown>(
  mutationFn: IdempotentMutationFn<TVars, TData>,
  options: UseIdempotentActionOptions<TVars, TData, TError>,
): UseIdempotentActionResult<TVars, TData, TError> {
  const { scope, scopeId, resetOnSuccess = true, onSuccess, onError, ...rest } = options;

  const [idempotencyKey, setIdempotencyKey] = useState(() =>
    idempotencyStore.getState().getOrCreate(scope, scopeId),
  );

  // Sync con el store cuando cambian scope/scopeId (derived state,
  // ver React docs: "Adjusting some state when a prop changes").
  const [prevScope, setPrevScope] = useState(scope);
  const [prevScopeId, setPrevScopeId] = useState(scopeId);
  if (scope !== prevScope || scopeId !== prevScopeId) {
    setPrevScope(scope);
    setPrevScopeId(scopeId);
    const fresh = idempotencyStore.getState().getOrCreate(scope, scopeId);
    setIdempotencyKey((prev) => (prev === fresh ? prev : fresh));
  }

  const mutation = useMutation<TData, TError, TVars>({
    ...rest,
    mutationFn: (vars) => mutationFn(vars, { idempotencyKey }),
    retry: false,
    onSuccess: (...args: Parameters<NonNullable<typeof onSuccess>>) => {
      if (resetOnSuccess) {
        idempotencyStore.getState().clear(scope, scopeId);
        const fresh = idempotencyStore.getState().getOrCreate(scope, scopeId);
        setIdempotencyKey(fresh);
      }
      onSuccess?.(...args);
    },
    onError,
  });

  const reset = useCallback(() => {
    const newKey = idempotencyStore.getState().reset(scope, scopeId);
    setIdempotencyKey(newKey);
    mutation.reset();
  }, [scope, scopeId, mutation]);

  return {
    execute: mutation.mutate,
    executeAsync: mutation.mutateAsync,
    reset,
    idempotencyKey,
    status: mutation.status,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    data: mutation.data,
    error: mutation.error,
  };
}
